import React from "react";

export const PROFILE_FAV_SPORT_GRID_OPTIONS = [
  "Cricket",
  "Football",
  "Tennis",
  "Horse Racing",
  "Golf",
  "Kabaddi",
  "MMA",
  "F1",
] as const;

export type ProfileFavSportOption = (typeof PROFILE_FAV_SPORT_GRID_OPTIONS)[number];

const OPTION_SET = new Set<string>(PROFILE_FAV_SPORT_GRID_OPTIONS);

/** Maps API / trader `match.sport` slug to profile catalogue name (for shared WebP assets). */
export function traderSportSlugToFavSportName(sport: string): string {
  const s = sport.trim().toLowerCase();
  if (s === "cricket") return "Cricket";
  if (s === "horses" || s === "horse") return "Horse Racing";
  if (s === "football") return "Football";
  if (s === "tennis") return "Tennis";
  return sport.trim() || sport;
}

/** Served from `frontend/static/assets` → `/assets/…` after build. */
const FAV_SPORT_ICON_SRC: Record<ProfileFavSportOption, string> = {
  Cricket: "/assets/cricket.webp",
  Football: "/assets/football.webp",
  Tennis: "/assets/tennis.webp",
  "Horse Racing": "/assets/horse-racing.webp",
  Golf: "/assets/golf.webp",
  Kabaddi: "/assets/kabaddi.webp",
  MMA: "/assets/mma.webp",
  F1: "/assets/f1.webp",
};

const DEFAULT_ICON_CLASS = "mx-auto h-8 w-8 shrink-0 sm:h-9 sm:w-9";

/**
 * Favourite-sport icons for the profile grid and chips.
 */
export function favSportIcon(
  sport: string,
  _color: string,
  className: string = DEFAULT_ICON_CLASS
): React.ReactNode {
  if (OPTION_SET.has(sport)) {
    const src = FAV_SPORT_ICON_SRC[sport as ProfileFavSportOption];
    return (
      <span
        className={`flex items-center justify-center ${className}`}
        aria-hidden
      >
        <img
          src={src}
          alt=""
          className="block h-full w-full min-h-0 min-w-0 object-contain object-center"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </span>
    );
  }
  return (
    <span className="font-anton text-[10px] font-normal uppercase leading-none text-white/50 [leading-trim:none]">
      {sport.slice(0, 3)}
    </span>
  );
}

export function parseFavSportsFromStorage(raw: string | undefined | null): string[] {
  if (!raw?.trim()) return [];
  const parts = raw.split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  const ordered: string[] = [];
  for (const opt of PROFILE_FAV_SPORT_GRID_OPTIONS) {
    if (parts.includes(opt)) ordered.push(opt);
  }
  for (const p of parts) {
    if (!OPTION_SET.has(p)) ordered.push(p);
  }
  return ordered;
}

/** Persist as comma-separated string; known sports in catalog order, then any legacy tokens. */
export function serializeFavSportsToStorage(selected: readonly string[]): string {
  if (selected.length === 0) return "";
  const set = new Set(selected);
  const known = PROFILE_FAV_SPORT_GRID_OPTIONS.filter((o) => set.has(o));
  const unknown = selected.filter((s) => !OPTION_SET.has(s));
  return [...known, ...unknown].join(", ");
}
